import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SaveIcon from '@mui/icons-material/Save';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import styles from './SampleEvaluationFormPage.module.css';

import {
  fetchExternalOrderDetails,
  ExternalOrderDetails,
  ExternalOrderItem,
} from './services/sampleAnalysisAPI';
import {
  fetchAvaliacaoPorPedido,
  salvarAvaliacao,
  AvaliacaoItem,
  NivelAvaliacao,
  ResultadoTeste,
  StatusAvaliacao,
} from './services/avaliacoesAPI';

// ──────────────────────────────────────────────────────────────────────────────
// CONSTANTES
// ──────────────────────────────────────────────────────────────────────────────

const ESCALA_LABELS: Record<number, string> = {
  1: 'Muito Ruim',
  2: 'Ruim',
  3: 'Regular',
  4: 'Bom',
  5: 'Ótimo',
};

const ERGONOMIA_CAMPOS: { key: keyof AvaliacaoItem; label: string; labels: string[] }[] = [
  { key: 'estetica',           label: 'Estética / Acabamento',   labels: ['Muito Bonita', 'Bonita', 'Tanto Faz', 'Feia', 'Muito Feia'] },
  { key: 'conforto_peito',     label: 'Conforto — Peito do Pé',  labels: ['Muito Justa', 'Justa', 'Confortável', 'Larga', 'Muito Larga'] },
  { key: 'conforto_dedos',     label: 'Conforto — Dedos',        labels: ['Muito Justa', 'Justa', 'Confortável', 'Larga', 'Muito Larga'] },
  { key: 'conforto_calcanhar', label: 'Conforto — Calcanhar',    labels: ['Muito Justa', 'Justa', 'Confortável', 'Larga', 'Muito Larga'] },
  { key: 'calcar',             label: 'Facilidade de Calçar',    labels: ['Muito Difícil', 'Difícil', 'Prática', 'Folgada', 'Muito Folgada'] },
  { key: 'descalcar',          label: 'Facilidade de Descalçar', labels: ['Muito Difícil', 'Difícil', 'Prática', 'Folgada', 'Muito Folgada'] },
  { key: 'aderencia',          label: 'Aderência do Solado',     labels: ['Muito Aderente', 'Aderente', 'Normal', 'Escorrega', 'Escorrega Muito'] },
  { key: 'temperatura',        label: 'Conforto Térmico',        labels: ['Muito Fria', 'Fria', 'Normal', 'Quente', 'Muito Quente'] },
  { key: 'peso',               label: 'Sensação de Peso',        labels: ['Muito Pesada', 'Pesada', 'Normal', 'Leve', 'Muito Leve'] },
  { key: 'limpeza',            label: 'Facilidade de Limpeza',   labels: ['Normal', 'Fácil Limpar\nSuja Pouco', 'Difícil Limpar\nSuja Pouco', 'Fácil Limpar\nSuja Muito', 'Difícil Limpar\nSuja Muito'] },
];

const CRITERIOS_GERAIS: { key: keyof AvaliacaoItem; label: string }[] = [
  { key: 'avaliacao_conforto',          label: 'Conforto Geral' },
  { key: 'avaliacao_durabilidade',      label: 'Durabilidade' },
  { key: 'avaliacao_protecao',          label: 'Proteção' },
  { key: 'avaliacao_custo_beneficio',   label: 'Custo-Benefício' },
];

const NIVEIS: NivelAvaliacao[] = ['ruim', 'regular', 'bom', 'otimo'];
const NIVEL_LABELS: Record<NivelAvaliacao, string> = {
  ruim: 'Ruim', regular: 'Regular', bom: 'Bom', otimo: 'Ótimo',
};

// ──────────────────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────────────────

const createBlankItem = (referencia: string, pedidoItemId?: number): AvaliacaoItem => ({
  id: 0,
  avaliacao_id: 0,
  pedido_item_id: pedidoItemId ?? null,
  referencia,
  estetica: null, conforto_peito: null, conforto_dedos: null, conforto_calcanhar: null,
  calcar: null, descalcar: null, aderencia: null, temperatura: null, peso: null, limpeza: null,
  avaliacao_conforto: null, avaliacao_durabilidade: null,
  avaliacao_protecao: null, avaliacao_custo_beneficio: null,
  resultado_teste: null,
});

const formatDate = (d?: string | null) => {
  if (!d) return '--';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR');
};

const diasRestantes = (prazo?: string | null): number | null => {
  if (!prazo) return null;
  const diff = new Date(prazo).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

const SampleEvaluationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pedidoId = Number(id);

  /* ── state: dados do pedido ── */
  const [orderDetails, setOrderDetails] = useState<ExternalOrderDetails | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [isAwsEnv, setIsAwsEnv] = useState(true); // Assume true unless API says otherwise

  /* ── state: avaliação ── */
  const [avaliacaoId, setAvaliacaoId] = useState<number | null>(null);
  const [statusAvaliacao, setStatusAvaliacao] = useState<StatusAvaliacao>('pendente');
  const [resultado, setResultado] = useState<ResultadoTeste | null>(null);
  const [dataPrazo, setDataPrazo] = useState<string | null>(null);

  /* ── state: campos do cabeçalho da ficha ── */
  const [tecnico,       setTecnico]       = useState('');
  const [representante, setRepresentante] = useState('');
  const [usuario,       setUsuario]       = useState('');
  const [funcao,        setFuncao]        = useState('');
  const [setor,         setSetor]         = useState('');
  const [parametro,     setParametro]     = useState('');
  const [dataInicio,    setDataInicio]    = useState('');
  const [dataFim,       setDataFim]       = useState('');
  const [observacoes,   setObservacoes]   = useState('');
  const [reclamacoes,   setReclamacoes]   = useState('');

  /* ── state: itens de avaliação (um por referência) ── */
  const [itens, setItens]           = useState<AvaliacaoItem[]>([]);
  const [activeItemIdx, setActiveItemIdx] = useState(0);

  /* ── state: UI ── */
  const [saving, setSaving]   = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // ── Carrega dados do pedido ──
  useEffect(() => {
    if (!pedidoId) return;
    (async () => {
      try {
        setLoadingOrder(true);
        const [orderRes, avalRes] = await Promise.all([
          fetchExternalOrderDetails(pedidoId),
          fetchAvaliacaoPorPedido(pedidoId),
        ]);

        const details = orderRes.data;
        setOrderDetails(details);

        // Inicializa itens (um por referência distinta do pedido)
        const refs: ExternalOrderItem[] = details.itens_pedido ?? [];
        const distRefs = Array.from(new Map(refs.map(r => [r.referencia, r])).values());

        const aval = avalRes.data.avaliacao;
        const existingItens = avalRes.data.itens ?? [];
        setIsAwsEnv(!!avalRes.data.is_aws_env);

        if (aval) {
          setAvaliacaoId(aval.id);
          setStatusAvaliacao(aval.status_avaliacao);
          setDataPrazo(aval.data_prazo_retorno);
          setTecnico(aval.tecnico_seguranca ?? '');
          setRepresentante(aval.representante ?? '');
          setUsuario(aval.usuario_teste ?? '');
          setFuncao(aval.funcao_usuario ?? '');
          setSetor(aval.setor_usuario ?? '');
          setParametro(aval.parametro_comparacao ?? '');
          setDataInicio(aval.data_inicio_teste?.slice(0, 10) ?? '');
          setDataFim(aval.data_fim_teste?.slice(0, 10) ?? '');
          setObservacoes(aval.observacoes ?? '');
          setReclamacoes(aval.reclamacoes ?? '');
          setResultado(aval.resultado_teste ?? null);
        }

        // Monta os itens (usa existente se já preenchido, caso contrário cria em branco)
        const mappedItens: AvaliacaoItem[] = distRefs.map(ref => {
          const saved = existingItens.find(i => i.referencia === ref.referencia);
          return saved ?? createBlankItem(ref.referencia, ref.id);
        });
        setItens(mappedItens.length > 0 ? mappedItens : [createBlankItem('REF-MANUAL')]);

      } catch (err) {
        console.error(err);
        setFeedback({ type: 'error', msg: 'Erro ao carregar os dados do pedido.' });
      } finally {
        setLoadingOrder(false);
      }
    })();
  }, [pedidoId]);

  // ── Atualiza campo de um item (escala 1-5 ou nível) ──
  const updateItem = useCallback(<K extends keyof AvaliacaoItem>(
    idx: number, key: K, value: AvaliacaoItem[K]
  ) => {
    setItens(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  // ── Salva a ficha ──
  const handleSave = async (finalStatus: StatusAvaliacao = 'em_teste') => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = {
        tecnico_seguranca: tecnico,
        representante,
        usuario_teste: usuario,
        funcao_usuario: funcao,
        setor_usuario: setor,
        parametro_comparacao: parametro,
        data_inicio_teste: dataInicio || undefined,
        data_fim_teste: dataFim || undefined,
        resultado_teste: resultado || undefined,
        observacoes,
        reclamacoes,
        status_avaliacao: finalStatus,
        itens,
      };
      const res = await salvarAvaliacao(pedidoId, payload);
      setAvaliacaoId(res.data.avaliacaoId);
      setStatusAvaliacao(finalStatus);
      setFeedback({ type: 'success', msg: res.data.message });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.response?.data?.error ?? 'Erro ao salvar avaliação.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Status banner ──
  const getBannerClass = () => {
    if (statusAvaliacao === 'expirada')   return styles.statusBannerExpirada;
    if (statusAvaliacao === 'preenchida') return styles.statusBannerPreenchida;
    if (statusAvaliacao === 'em_teste')   return styles.statusBannerEmTeste;
    return styles.statusBannerPendente;
  };

  const getBannerText = () => {
    const dias = diasRestantes(dataPrazo);
    if (statusAvaliacao === 'preenchida') return '✅ Ficha preenchida e enviada';
    if (statusAvaliacao === 'expirada')   return '⚠️ Ficha vencida — preencha urgentemente';
    if (statusAvaliacao === 'em_teste')   return '🔄 Teste em andamento';
    return '⏳ Aguardando início do preenchimento';
  };

  if (loadingOrder) {
    return (
      <div className={styles.pageContainer}>
        <p style={{ color: '#64748b', textAlign: 'center', padding: '48px' }}>Carregando ficha...</p>
      </div>
    );
  }

  const pedido = orderDetails?.pedido_externo;
  const amostra = orderDetails?.amostra_pedido;
  const cliente = orderDetails?.cliente;
  const dias = diasRestantes(dataPrazo);
  const activeItem = itens[activeItemIdx];

  return (
    <div className={styles.pageContainer}>
      {/* ── HEADER ── */}
      <div className={styles.pageHeader}>
        <button className={styles.backButton} onClick={() => navigate(`/amostras-pedidos/${id}`)}>
          <ArrowBackIcon fontSize="small" /> Voltar
        </button>
        <h1>Ficha de Avaliação — Pedido #{pedido?.id ?? id}</h1>
      </div>

      {/* ── ALERTA DE SOMENTE LEITURA ── */}
      {!isAwsEnv && (
        <div className={styles.feedbackMsg} style={{ backgroundColor: '#e2e8f0', color: '#334155', borderLeftColor: '#64748b' }}>
          <strong>Aviso:</strong> Você está no ambiente da fábrica. Esta ficha é de visualização (somente leitura). O preenchimento deve ser feito pelo consultor através da plataforma AWS.
        </div>
      )}

      {/* ── STATUS BANNER ── (Apenas na AWS) */}
      {isAwsEnv && dataPrazo && (
        <div className={`${styles.statusBanner} ${getBannerClass()}`}>
          <AccessTimeIcon fontSize="small" />
          <span>{getBannerText()}</span>
          <span className={styles.prazoInfo}>
            Prazo de retorno: {formatDate(dataPrazo)}
            {dias !== null && statusAvaliacao !== 'preenchida' && (
              <> &nbsp;·&nbsp; {dias >= 0 ? `${dias} ${dias === 1 ? 'dia restante' : 'dias restantes'}` : `Vencida há ${Math.abs(dias)} ${Math.abs(dias) === 1 ? 'dia' : 'dias'}`}</>
            )}
          </span>
        </div>
      )}

      {/* ── FEEDBACK ── */}
      {feedback && (
        <div className={`${styles.feedbackMsg} ${feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError}`}>
          {feedback.msg}
        </div>
      )}

      {/* ════════════════════════════════════════════
          CARD 1 — DADOS DO PEDIDO (somente leitura)
          ════════════════════════════════════════════ */}
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><AssignmentIcon fontSize="small" /></span>
          <h2>Dados do Pedido</h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Cliente</label>
              <span>{pedido?.cliente_razao_social ?? cliente?.NOME ?? '--'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>CNPJ</label>
              <span>{pedido?.cliente_cnpj ?? cliente?.DOCUMENTO ?? '--'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Cidade / UF</label>
              <span>{pedido?.cliente_cidade ?? '--'} / {pedido?.cliente_estado ?? '--'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Vendedor</label>
              <span>{cliente?.VENDEDOR_NOME ?? '--'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Segmento</label>
              <span>{cliente?.SEGMENTO_CLASSE ?? '--'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Operação Fiscal</label>
              <span>{pedido?.operacao_fiscal_desc ?? '--'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Data de Emissão</label>
              <span>{formatDate(pedido?.data_emissao)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Calçado Sugerido</label>
              <span>{amostra?.calcado_sugerido ?? '--'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          CARD 2 — PARTICIPANTES DO TESTE
          ════════════════════════════════════════════ */}
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><PersonOutlineIcon fontSize="small" /></span>
          <h2>Participantes do Teste</h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Técnico de Segurança</label>
              <input value={tecnico} onChange={e => setTecnico(e.target.value)} placeholder="Nome do técnico" disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Representante / Consultor</label>
              <input value={representante} onChange={e => setRepresentante(e.target.value)} placeholder="Nome do representante" disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Usuário do Teste (quem usou)</label>
              <input value={usuario} onChange={e => setUsuario(e.target.value)} placeholder="Nome do colaborador" disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Função do Usuário</label>
              <input value={funcao} onChange={e => setFuncao(e.target.value)} placeholder="Ex: Operador de máquina" disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Setor</label>
              <input value={setor} onChange={e => setSetor(e.target.value)} placeholder="Ex: Produção, Logística..." disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Parâmetro de Comparação (marcas atuais)</label>
              <input value={parametro} onChange={e => setParametro(e.target.value)} placeholder="Marcas/modelos que usa atualmente" disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Início do Teste</label>
              <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} disabled={!isAwsEnv} />
            </div>
            <div className={styles.formGroup}>
              <label>Fim do Teste / Data do Preenchimento</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} disabled={!isAwsEnv} />
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          CARD 3 — AVALIAÇÃO POR REFERÊNCIA
          ════════════════════════════════════════════ */}
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><InventoryIcon fontSize="small" /></span>
          <h2>Avaliação por Referência</h2>
        </div>
        <div className={styles.cardBody}>
          {/* Tabs de produto */}
          {itens.length > 1 && (
            <div className={styles.productTabs}>
              {itens.map((item, idx) => (
                <button
                  key={item.referencia}
                  className={`${styles.productTab} ${idx === activeItemIdx ? styles.productTabActive : ''}`}
                  onClick={() => setActiveItemIdx(idx)}
                >
                  {item.referencia}
                  {item.resultado_teste && (
                    <span className={item.resultado_teste === 'aprovado' ? styles.tabBadgeAprovado : styles.tabBadgeReprovado}>
                      {item.resultado_teste === 'aprovado' ? '✓' : '✗'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Header da referência ativa */}
          {activeItem && (
            <>
              <div className={styles.refHeader}>
                <div className={styles.refHeaderTitle}>
                  <span className={styles.refBadge}>{activeItem.referencia}</span>
                  <span className={styles.refDesc}>
                    {orderDetails?.itens_pedido.find(i => i.referencia === activeItem.referencia)?.descricao ?? ''}
                  </span>
                </div>
                {activeItem.resultado_teste && (
                  <span className={`${styles.statusBadge} ${activeItem.resultado_teste === 'aprovado' ? styles.statusBadgeAprovado : styles.statusBadgeReprovado}`}>
                    {activeItem.resultado_teste === 'aprovado' ? 'APROVADO NO TESTE' : 'REPROVADO NO TESTE'}
                  </span>
                )}
              </div>

              {/* Grade Ergonomia 1-5 */}
              <div className={styles.scaleGrid}>
                {ERGONOMIA_CAMPOS.map(({ key, label, labels }) => {
                  const val = activeItem[key] as number | null;
                  return (
                    <div key={key} className={styles.scaleRow}>
                      <span className={styles.scaleLabel}>{label}</span>
                      <div className={styles.scaleOptions}>
                        {[1, 2, 3, 4, 5].map(n => (
                          <label
                            key={n}
                            className={`${styles.scaleOption} ${val === n ? styles.scaleOptionSelected : ''}`}
                            title={labels[n - 1]}
                          >
                            <input
                              type="radio"
                              name={`${activeItem.referencia}_${key}`}
                              value={n}
                              checked={val === n}
                              disabled={!isAwsEnv}
                              onChange={() => updateItem(activeItemIdx, key, n as any)}
                            />
                            <div className={styles.scaleOptionBtn}></div>
                            <span className={styles.scaleOptionLabel} style={{ whiteSpace: 'pre-line' }}>{labels[n - 1]}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.divider} />

              {/* Matriz de Avaliação Geral */}
              <div className={styles.matrizTableWrapper}>
                <table className={styles.matrizTable}>
                  <thead>
                    <tr>
                      <th>Critério</th>
                      {NIVEIS.map(n => <th key={n}>{NIVEL_LABELS[n]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {CRITERIOS_GERAIS.map(({ key, label }) => {
                      const val = activeItem[key] as NivelAvaliacao | null;
                      return (
                        <tr key={key}>
                          <td>{label}</td>
                          {NIVEIS.map(nivel => (
                            <td key={nivel}>
                              <label className={styles.matrizRadio}>
                                <input
                                  type="radio"
                                  name={`${activeItem.referencia}_${key}`}
                                  checked={val === nivel}
                                  disabled={!isAwsEnv}
                                  onChange={() => updateItem(activeItemIdx, key, nivel as any)}
                                />
                                <div className={`${styles.matrizRadioBtn} ${val === nivel ? styles.matrizRadioBtnSelected : ''}`} />
                              </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Resultado Individual por Referência */}
              <div className={`${styles.formGroup} ${styles.fullWidth}`} style={{ marginTop: '24px' }}>
                <label>Resultado para {activeItem.referencia}</label>
                <div className={styles.resultadoOptions}>
                  <button
                    type="button"
                    className={`${styles.resultadoBtn} ${styles.resultadoBtnAprovado} ${activeItem.resultado_teste === 'aprovado' ? styles.resultadoBtnAprovadoSelected : ''}`}
                    onClick={() => updateItem(activeItemIdx, 'resultado_teste', activeItem.resultado_teste === 'aprovado' ? null : 'aprovado')}
                    disabled={!isAwsEnv}
                  >
                    ✅ Aprovado — Atende às necessidades
                  </button>
                  <button
                    type="button"
                    className={`${styles.resultadoBtn} ${styles.resultadoBtnReprovado} ${activeItem.resultado_teste === 'reprovado' ? styles.resultadoBtnReprovadoSelected : ''}`}
                    onClick={() => updateItem(activeItemIdx, 'resultado_teste', activeItem.resultado_teste === 'reprovado' ? null : 'reprovado')}
                    disabled={!isAwsEnv}
                  >
                    ❌ Reprovado — Não atende
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════
          CARD 4 — OBSERVAÇÕES E RESULTADO FINAL
          ════════════════════════════════════════════ */}
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <span className={styles.cardIcon}><ChecklistIcon fontSize="small" /></span>
          <h2>Resultado Final & Observações</h2>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.formGrid}>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>Observações Gerais</label>
              <textarea
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                placeholder="Comentários adicionais, pontos de atenção, sugestões de melhoria..."
                rows={3}
                disabled={statusAvaliacao === 'preenchida'}
              />
            </div>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>Reclamações / Problemas encontrados</label>
              <textarea
                value={reclamacoes}
                onChange={e => setReclamacoes(e.target.value)}
                placeholder="Descreva aqui qualquer reclamação ou problema relatado pelo cliente..."
                rows={3}
                disabled={statusAvaliacao === 'preenchida'}
              />
            </div>
          </div>
        </div>

        {/* ── AÇÕES ── */}
        {isAwsEnv && statusAvaliacao !== 'preenchida' && (
          <div className={styles.footerActions}>
            <button
              className={styles.btnSecondary}
              onClick={() => handleSave('em_teste')}
              disabled={saving}
            >
              💾 Salvar Rascunho
            </button>
            <button
              className={styles.btnSave}
              onClick={() => setShowConfirmDialog(true)}
              disabled={saving || !itens.every(i => i.resultado_teste)}
              title={!itens.every(i => i.resultado_teste) ? "Defina o Resultado (Aprovado/Reprovado) para todas as referências antes de enviar" : ""}
            >
              <SaveIcon fontSize="small" />
              {saving ? 'Enviando...' : 'Enviar Ficha'}
            </button>
          </div>
        )}
      </div>

      {/* DIÁLOGO DE CONFIRMAÇÃO DE ENVIO */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Confirmar Envio da Avaliação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja enviar esta ficha de avaliação? 
            Uma vez enviada, os dados não poderão ser alterados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="inherit" disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setShowConfirmDialog(false);
              handleSave('preenchida');
            }}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            Confirmar Envio
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SampleEvaluationFormPage;
